import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, ShieldCheck, Bot } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { devError } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    key: "name",
    botText: "Hi 👋 I'm Sofia from ProsoftMarketplace. What's your name?",
    placeholder: "Jane Doe",
    type: "text",
    validate: (v) => v.trim().length > 1,
    errorMsg: "Please enter your name.",
  },
  {
    key: "email",
    botText: (form) => `Thanks ${form.name.split(" ")[0]}! What's the best email to reply on?`,
    placeholder: "you@example.com",
    type: "email",
    validate: (v) => /^\S+@\S+\.\S+$/.test(v.trim()),
    errorMsg: "That email doesn't look right.",
  },
  {
    key: "phone",
    botText: "Perfect. Your phone number, in case we need to call?",
    placeholder: "+1 (555) 123-4567",
    type: "tel",
    validate: (v) => v.replace(/\D/g, "").length >= 7,
    errorMsg: "Please enter a valid phone number.",
  },
  {
    key: "message",
    botText: "Great. What can we help you with?",
    placeholder: "Tell us how we can help…",
    type: "textarea",
    validate: (v) => v.trim().length > 3,
    errorMsg: "Just a few more words so we can help.",
  },
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [done, setDone] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const step = STEPS[stepIdx];

  // Seed with greeting when opened first time
  useEffect(() => {
    if (open && messages.length === 0) {
      const bot = typeof STEPS[0].botText === "function" ? STEPS[0].botText(form) : STEPS[0].botText;
      setMessages([{ from: "bot", text: bot }]);
    }
  }, [open, messages.length, form]);

  // Auto scroll on new messages
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, done]);

  // Autofocus input when a new step begins
  useEffect(() => {
    if (open && inputRef.current && !done) inputRef.current.focus();
  }, [open, stepIdx, done]);

  const sendAnswer = async () => {
    const value = draft;
    if (!step || !step.validate(value)) {
      toast.error(step?.errorMsg || "Please provide a valid answer.");
      return;
    }
    const nextForm = { ...form, [step.key]: value.trim() };
    setForm(nextForm);
    setMessages((m) => [...m, { from: "user", text: value.trim() }]);
    setDraft("");

    if (stepIdx < STEPS.length - 1) {
      const nextStep = STEPS[stepIdx + 1];
      const botText =
        typeof nextStep.botText === "function" ? nextStep.botText(nextForm) : nextStep.botText;
      // Small "typing" delay for realism
      setTimeout(() => {
        setMessages((m) => [...m, { from: "bot", text: botText, typing: true }]);
      }, 250);
      setTimeout(() => {
        setMessages((m) =>
          m.map((msg, i) => (i === m.length - 1 ? { ...msg, typing: false } : msg)),
        );
        setStepIdx(stepIdx + 1);
      }, 900);
      return;
    }

    // Last step — submit
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: nextForm.name,
        email: nextForm.email,
        phone: nextForm.phone,
        subject: "Chat inquiry from website",
        message: nextForm.message,
      });
      setDone(true);
      setMessages((m) => [
        ...m,
        {
          from: "bot",
          text: `Thanks ${nextForm.name.split(" ")[0]}! Someone from our team will reach out to ${nextForm.email} within an hour.`,
        },
      ]);
    } catch (err) {
      devError("Chat submit failed:", err);
      toast.error("Couldn't send. Please email support@prosoftmarketplace.com.");
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && step?.type !== "textarea") {
      e.preventDefault();
      sendAnswer();
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && step?.type === "textarea") {
      e.preventDefault();
      sendAnswer();
    }
  };

  const reset = () => {
    setStepIdx(0);
    setMessages([]);
    setDraft("");
    setForm({ name: "", email: "", phone: "", message: "" });
    setDone(false);
  };

  const progress = useMemo(() => `${Math.min(stepIdx + (done ? 1 : 0), STEPS.length)} / ${STEPS.length}`, [stepIdx, done]);

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-open-btn"
          aria-label="Open chat"
          className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[6px_6px_0_0_rgba(15,23,42,1)] transition-transform hover:-translate-y-0.5 hover:bg-emerald-600"
        >
          <MessageCircle className="h-6 w-6" strokeWidth={2.5} />
          <span className="pointer-events-none absolute -right-1 -top-1 flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400" />
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby="chat-widget-title"
          data-testid="chat-panel"
          className="fixed bottom-4 right-4 z-50 flex h-[560px] w-[calc(100%-2rem)] max-w-sm flex-col border-2 border-slate-900 bg-white shadow-[10px_10px_0_0_rgba(16,185,129,1)] sm:bottom-6 sm:right-6"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-950 px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center bg-emerald-500">
                <Bot className="h-5 w-5 text-white" />
              </span>
              <div>
                <div id="chat-widget-title" className="font-display text-sm font-bold tracking-tight">
                  Chat with Sofia
                </div>
                <div className="flex items-center gap-1 text-[10px] text-emerald-300">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                  Online · Replies in minutes
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden text-[10px] font-mono text-slate-400 sm:inline">{progress}</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="chat-close-btn"
                aria-label="Close chat"
                className="p-1 text-slate-300 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4">
            {messages.map((m, i) => (
              <Message key={i} from={m.from} typing={m.typing} text={m.text} />
            ))}
            {done && (
              <div className="mt-4 border border-emerald-500 bg-emerald-50 p-3 text-xs text-slate-700">
                <div className="flex items-center gap-1 font-bold uppercase tracking-wider text-emerald-700">
                  <ShieldCheck className="h-3 w-3" /> Message received
                </div>
                <p className="mt-1">
                  Prefer to talk now? Call{" "}
                  <a href="tel:+18886554464" className="font-bold text-emerald-700 underline">
                    +1 (888) 655-4464
                  </a>
                  .
                </p>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="chat-restart-btn"
                  className="mt-2 text-xs font-bold uppercase tracking-wider text-slate-700 underline hover:text-slate-900"
                >
                  Start another chat
                </button>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Composer */}
          {!done && (
            <div className="border-t border-slate-200 bg-white p-3">
              {step?.type === "textarea" ? (
                <Textarea
                  ref={inputRef}
                  data-testid="chat-input"
                  placeholder={step.placeholder}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKeyDown}
                  rows={2}
                  className="resize-none rounded-sm border-slate-300 focus-visible:ring-emerald-500"
                />
              ) : (
                <Input
                  ref={inputRef}
                  data-testid="chat-input"
                  type={step?.type || "text"}
                  placeholder={step?.placeholder}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKeyDown}
                  autoComplete={
                    step?.key === "email"
                      ? "email"
                      : step?.key === "phone"
                        ? "tel"
                        : step?.key === "name"
                          ? "name"
                          : "off"
                  }
                  className="rounded-sm border-slate-300 focus-visible:ring-emerald-500"
                />
              )}
              <div className="mt-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">
                  {step?.key} · required
                </div>
                <Button
                  type="button"
                  onClick={sendAnswer}
                  disabled={submitting || !draft.trim()}
                  data-testid="chat-send-btn"
                  className="h-8 rounded-sm bg-slate-900 px-3 text-xs font-bold text-white hover:bg-slate-800 disabled:opacity-40"
                >
                  {submitting ? "Sending…" : (
                    <>
                      Send <Send className="ml-1 h-3 w-3" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const Message = ({ from, text, typing }) => {
  if (from === "bot") {
    return (
      <div className="flex items-start gap-2" data-testid="chat-msg-bot">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center bg-slate-900">
          <Bot className="h-3.5 w-3.5 text-emerald-400" />
        </span>
        <div className="max-w-[85%] border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800">
          {typing ? (
            <span className="inline-flex gap-1">
              <Dot delay="0ms" />
              <Dot delay="150ms" />
              <Dot delay="300ms" />
            </span>
          ) : (
            text
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start justify-end gap-2" data-testid="chat-msg-user">
      <div className="max-w-[85%] bg-emerald-500 px-3 py-2 text-sm text-white">{text}</div>
    </div>
  );
};

const Dot = ({ delay }) => (
  <span
    className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400"
    style={{ animationDelay: delay }}
  />
);
